// src/encounter/lib/questionnaire-item-code.ts
// [PROD-3376] Does the served template carry an item bound to a given terminology code, at any depth?
//
// Used by the encounter page to learn whether the template already draws a section the encounter
// would otherwise draw a second time. Today: LOINC 75305-3 (the nutrition care process the dietitian
// template authors), which makes the plan step's free-text "Nutrition care process" card a duplicate.
//
// Reads `QItem.code` — the FHIR `item.code` the mapper carries through (fhir-to-ds-questionnaire.ts).
// Pure; never throws; an absent questionnaire or an item with no codes is simply "no".
import type { Questionnaire, QItem } from '@scripsteam/scrips-signal-ds';

/**
 * One spelling for a code system URI, so `http://loinc.org`, `https://loinc.org/`, `loinc.org` and
 * `LOINC.org` compare equal. Scheme and trailing slashes are dropped, case is folded.
 */
function normaliseSystem(system: string | undefined): string {
  return (system ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/\/+$/, '');
}

/** True when any item in the questionnaire, at any depth, carries `{ system, code }` in `code[]`. */
export function questionnaireHasItemCode(
  questionnaire: Questionnaire | undefined,
  system: string,
  code: string,
): boolean {
  if (!questionnaire) return false;
  const wantSystem = normaliseSystem(system);
  const wantCode = code.trim();
  const walk = (items: readonly QItem[] | undefined): boolean =>
    (items ?? []).some(
      (item) =>
        (item.code ?? []).some((c) => c.code?.trim() === wantCode && normaliseSystem(c.system) === wantSystem) ||
        walk(item.item),
    );
  return walk(questionnaire.item);
}
